import { Injectable } from "@angular/core";
import { inject } from "@angular/core";
import { PartnerApi } from "../Api/PartnerApi";
import { PartnerState } from "./PartnerState";
import { PartnerSearchRequest } from "../Dto/PartnerSearchRequest";
import { CreatePartnerRequest } from "../Dto/CreatePartnerRequest";
import { Partner } from "../Dto/PartnerModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";
import { PartnerPublicRequestLink, PartnerPublicRequestLinkGenerateResponse } from "../Dto/PartnerPublicRequestLink";

@Injectable({ providedIn: "root" })
export class PartnerEffects {
  private readonly partnerApi = inject(PartnerApi);
  private readonly partnerState = inject(PartnerState);

  async searchPartners(params: PartnerSearchRequest = {}): Promise<void> {
    const mergedParams: PartnerSearchRequest = {
      page: 1,
      limit: 10,
      ...params,
    };

    this.partnerState.setLoading(true);
    this.partnerState.setError(null);

    try {
      const response = await this.partnerApi.searchPartners(mergedParams);
      this.partnerState.setPartners(response.data, response.pagination);
      this.partnerState.setSearchParams(mergedParams);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
    } finally {
      this.partnerState.setLoading(false);
    }
  }

  async deletePartner(partnerId: string): Promise<boolean> {
    this.partnerState.setLoading(true);
    this.partnerState.setError(null);

    try {
      await this.partnerApi.deletePartner(partnerId);
      this.partnerState.removePartnerFromState(partnerId);
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.partnerState.setLoading(false);
    }
  }

  async createPartner(payload: CreatePartnerRequest): Promise<Partner> {
    this.partnerState.setLoading(true);
    this.partnerState.setError(null);

    try {
      const createdPartner = await this.partnerApi.createPartner(payload);
      this.partnerState.addPartnerToState(createdPartner);
      return createdPartner;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.partnerState.setLoading(false);
    }
  }

  async updatePartner(id: string, payload: Partial<CreatePartnerRequest>): Promise<Partner> {
    this.partnerState.setLoading(true);
    this.partnerState.setError(null);

    try {
      const updatedPartner = await this.partnerApi.updatePartner(id, payload);
      if (!updatedPartner) {
        throw new Error("Partner not found.");
      }
      this.partnerState.updatePartnerInState(updatedPartner);
      return updatedPartner;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.partnerState.setLoading(false);
    }
  }

  async getPartnerById(id: string): Promise<Partner | null> {
    this.partnerState.setLoading(true);
    this.partnerState.setError(null);

    try {
      const partner = await this.partnerApi.getPartnerById(id);
      return partner;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
      return null;
    } finally {
      this.partnerState.setLoading(false);
    }
  }

  async getPublicRequestLink(partnerId: string): Promise<PartnerPublicRequestLink> {
    this.partnerState.setError(null);

    try {
      return await this.partnerApi.getPublicRequestLink(partnerId);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }

  async generatePublicRequestLink(partnerId: string, warehouseId: string): Promise<PartnerPublicRequestLinkGenerateResponse> {
    this.partnerState.setError(null);

    try {
      return await this.partnerApi.generatePublicRequestLink(partnerId, warehouseId);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }

  async disablePublicRequestLink(partnerId: string): Promise<PartnerPublicRequestLink> {
    this.partnerState.setError(null);

    try {
      return await this.partnerApi.disablePublicRequestLink(partnerId);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.partnerState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }
}
